import React from 'react';
import { useTranslation } from 'react-i18next';
import type { NameComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';

interface NameSettingsProps {
  component: NameComponent;
}

export const NameSettings: React.FC<NameSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();

  const handleUpdate = (updates: Partial<NameComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.name.styleLabel')}</label>
        <select
          value={component.settings.style || 'default'}
          onChange={(e) => handleUpdate({ style: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="default">{t('settings.common.styles.default')}</option>
          <option value="modern">{t('settings.common.styles.modern')}</option>
          <option value="minimal">{t('settings.common.styles.minimal')}</option>
          <option value="bold">{t('settings.common.styles.bold')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.name.name')}</label>
        <input
          type="text"
          value={component.settings.name}
          onChange={(e) => handleUpdate({ name: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.name.namePlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.name.title')}</label>
        <input
          type="text"
          value={component.settings.title}
          onChange={(e) => handleUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.name.titlePlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.name.nameSize', { value: component.settings.nameSize })}
        </label>
        <input
          type="range"
          min="16"
          max="72"
          value={component.settings.nameSize}
          onChange={(e) => handleUpdate({ nameSize: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.name.titleSize', { value: component.settings.titleSize })}
        </label>
        <input
          type="range"
          min="12"
          max="32"
          value={component.settings.titleSize}
          onChange={(e) => handleUpdate({ titleSize: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.name.nameColor')}</label>
        <ColorPicker
          color={component.settings.nameColor}
          onChange={(color) => handleUpdate({ nameColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.name.titleColor')}</label>
        <ColorPicker
          color={component.settings.titleColor}
          onChange={(color) => handleUpdate({ titleColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.alignment')}</label>
        <select
          value={component.settings.alignment}
          onChange={(e) => handleUpdate({ alignment: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="left">{t('settings.common.alignLeft')}</option>
          <option value="center">{t('settings.common.alignCenter')}</option>
          <option value="right">{t('settings.common.alignRight')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.paddingPx', { value: component.settings.padding })}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  );
};
